import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChatWindow } from "./types";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  IconArrowLeft,
  LoadingSpinner,
  PrimaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import TabBar from "./Tabs/TabBar";
import Appearance from "./Tabs/Appearance/Appearance";
import Widgets from "./Tabs/Widgets/Widgets";
import Channels from "./Tabs/Channels/Channels";
import PreChatSurvey from "./Tabs/PreChatSurvey";
import PostChatSurvey from "./Tabs/PostChatSurvey";
import Settings from "./Tabs/Settings/Settings";
import Deploy from "./Tabs/Deploy/Deploy";
import { LanguageConfigModel, StyleConfigModel } from "../types";
import Edit from "@/kb/articles/Edit";
import EditableLabel from "./EditableLabel";

const defaultChatWindow: ChatWindow = {
  id: 0,
  siteId: 0,
  name: "",
  updateTimestamp: "",
  createdByUserName: "",
  styleConfig: {} as StyleConfigModel,
  languageConfig: {} as LanguageConfigModel
};

const tabs = [
  "Appearance",
  "Widgets",
  "Channels",
  "Deploy"
  // "Pre-chat Survey",
  // "Post-chat Survey",
  // "Settings"
];

export default function ChatWindowEditor() {
  const { chatWindowId: stringChatWindowId } = useParams<{
    chatWindowId: string;
  }>();
  const [chatWindow, setChatWindow] = useState<ChatWindow>(defaultChatWindow);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [workingChatWindow, setWorkingChatWindow] =
    useState<ChatWindow>(defaultChatWindow);
  const [saving, setSaving] = useState(false);
  const [stagedLogoFile, setStagedLogoFile] = React.useState<File | null>(null);
  const [stagedGraphicFile, setStagedGraphicFile] = React.useState<File | null>(null);
  const [stagedHeaderBgFile, setStagedHeaderBgFile] = React.useState<File | null>(null);

  const api = useApi();
  const toastAlert = useToastAlert();
  const navigate = useNavigate();

  useEffect(() => {
    if (!stringChatWindowId) {
      console.error("Chat window ID is required");
      return;
    }

    async function fetchChatWindow() {
      setLoading(true);
      const chatWindowId = Number(stringChatWindowId);
      if (isNaN(chatWindowId)) {
        toastAlert.displayToast(Severity.Error, "Invalid chat window ID.");
        setLoading(false);
        return;
      }
      const response = await api.messaging.get(
        `ChatWindow/Get/${chatWindowId}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        toastAlert.displayToast(Severity.Error, errorText);
        setLoading(false);
        return;
      }
      const chatWindow = (await response.json()) as ChatWindow;
      chatWindow.styleConfig = chatWindow.styleConfig || ({} as StyleConfigModel);
      chatWindow.languageConfig = chatWindow.languageConfig || ({} as LanguageConfigModel);
      setChatWindow(chatWindow);
      setWorkingChatWindow(chatWindow);
      setLoading(false);
    }
    fetchChatWindow();
  }, [api.messaging, stringChatWindowId, toastAlert]);

  async function saveChanges() {
    setSaving(true);
    const formData = new FormData();
    if (stagedLogoFile) {
      formData.append("logo", stagedLogoFile);
      workingChatWindow.styleConfig.logoUrl = undefined;
    }
    if (stagedGraphicFile) {
      formData.append("queueGraphic", stagedGraphicFile);
      workingChatWindow.styleConfig.queueGraphicUrl = undefined;
    }
    if (stagedHeaderBgFile) {
      formData.append("headerBgImage", stagedHeaderBgFile);
      workingChatWindow.styleConfig.headerBackgroundImageUrl = undefined;
    }
    formData.append("name", workingChatWindow.name);
    formData.append(
      "styleConfig",
      JSON.stringify(workingChatWindow.styleConfig)
    );
    formData.append(
      "languageConfig",
      JSON.stringify(workingChatWindow.languageConfig)
    );
    const response = await api.messaging.postFormData(
      `ChatWindow/Update/${workingChatWindow.id}`,
      formData
    );

    if (!response.ok) {
      const errorText = await response.text();
      toastAlert.displayToast(Severity.Error, errorText);
      setSaving(false);
      return;
    }

    toastAlert.displayToast(
      Severity.Success,
      "Chat window updated successfully"
    );

    const responseChatWindow = (await response.json()) as ChatWindow;
    responseChatWindow.styleConfig = responseChatWindow.styleConfig || ({} as StyleConfigModel);
    responseChatWindow.languageConfig = responseChatWindow.languageConfig || ({} as LanguageConfigModel);
    setChatWindow(responseChatWindow);
    setWorkingChatWindow(responseChatWindow);
    setSaving(false);
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div
        className="sticky z-10 bg-slate-50 -m-6 px-6 pt-6"
        style={{ top: "-24px" }}
      >
        <div className="flex justify-between">
          <div className="flex items-center">
            <IconArrowLeft
              className="text-slate-500 hover:cursor-pointer"
              onClick={() => navigate(-1)}
            />
            <EditableLabel
              value={workingChatWindow.name}
              onChange={(newValue) =>
                setWorkingChatWindow({ ...workingChatWindow, name: newValue })
              }
            />
          </div>
          <PrimaryBlueButton
            label="Save"
            onClick={() => {
              saveChanges();
            }}
          />
        </div>
        <TabBar
          tabs={tabs}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />
      </div>
      <div className="p-4 mt-6">
        {selectedTab === "Appearance" && (
          <Appearance
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
            setStagedLogoFile={setStagedLogoFile}
            setStagedGraphicFile={setStagedGraphicFile}
            setStagedHeaderBgFile={setStagedHeaderBgFile}
          />
        )}
        {selectedTab === "Widgets" && (
          <Widgets
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
          />
        )}
        {selectedTab === "Channels" && (
          <Channels
            workingChatWindow={workingChatWindow}
            setWorkingChatWindow={setWorkingChatWindow}
          />
        )}
        {selectedTab === "Deploy" && (
          <Deploy chatWindow={chatWindow} />
        )}
        {/* {selectedTab === "Pre-chat Survey" && <PreChatSurvey />}
      {selectedTab === "Post-chat Survey" && <PostChatSurvey />}
      {selectedTab === "Settings" && <Settings />} */}
      </div>
    </div>
  );
}
